// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * The ggml type table — the most load-bearing numbers in this tool.
 *
 * Each quantised type packs `blockSize` weights into `typeSize` bytes, so a
 * tensor's storage is exactly `(elements / blockSize) * typeSize`. The block
 * struct arithmetic is spelled out rather than pre-multiplied so each entry can
 * be checked against `ggml/src/ggml-common.h` by eye.
 *
 * Verified two independent ways on 2026-08-02: by compiling ggml-common.h and
 * printing `sizeof` for every block struct (its `_Static_assert`s pass), and by
 * diffing against llama.cpp's own `gguf-py/gguf/constants.py`. Both agree, and
 * the resulting sizes reconcile byte-exactly (delta = 0) against the
 * Content-Length of 20 real GGUF files spanning F32, F16, BF16, I64, Q4_0,
 * Q5_0, Q5_1, Q8_0, Q2_K, Q3_K, Q4_K, Q5_K, Q6_K, IQ1_S, IQ1_M, IQ2_XXS,
 * IQ2_XS, IQ3_XXS, IQ3_S, IQ4_NL, IQ4_XS and MXFP4.
 *
 * The enum is append-only by an explicit upstream guarantee ("always add types
 * at the end of the enum to keep backward compatibility"), so old files always
 * parse correctly against a newer table.
 */

/** Super-block size shared by every K- and IQ-quant. */
const QK_K = 256;

export interface GgmlType {
  id: number;
  name: string;
  /** Weights per block. */
  blockSize: number;
  /** Bytes per block. */
  typeSize: number;
  /** True for types that only ever exist as activations, never stored in a file. */
  activationOnly?: boolean;
  /** Set for types added recently enough that a file using them is unusual. */
  recent?: boolean;
}

function t(id: number, name: string, blockSize: number, typeSize: number, extra: Partial<GgmlType> = {}): GgmlType {
  return { id, name, blockSize, typeSize, ...extra };
}

export const GGML_TYPES: Record<number, GgmlType> = {
  0: t(0, 'F32', 1, 4),
  1: t(1, 'F16', 1, 2),
  2: t(2, 'Q4_0', 32, 2 + 16),
  3: t(3, 'Q4_1', 32, 2 + 2 + 16),
  // 4 = Q4_2 and 5 = Q4_3 were removed upstream; see DEPRECATED_TYPES below.
  6: t(6, 'Q5_0', 32, 2 + 4 + 16),
  7: t(7, 'Q5_1', 32, 2 + 2 + 4 + 16),
  8: t(8, 'Q8_0', 32, 2 + 32),
  9: t(9, 'Q8_1', 32, 4 + 4 + 32, { activationOnly: true }),
  10: t(10, 'Q2_K', QK_K, 2 + 2 + QK_K / 16 + QK_K / 4),
  11: t(11, 'Q3_K', QK_K, 2 + QK_K / 4 + QK_K / 8 + 12),
  12: t(12, 'Q4_K', QK_K, 2 + 2 + QK_K / 2 + 12),
  13: t(13, 'Q5_K', QK_K, 2 + 2 + QK_K / 2 + QK_K / 8 + 12),
  14: t(14, 'Q6_K', QK_K, 2 + QK_K / 2 + QK_K / 4 + QK_K / 16),
  15: t(15, 'Q8_K', QK_K, 4 + QK_K + QK_K / 8, { activationOnly: true }),
  16: t(16, 'IQ2_XXS', QK_K, 2 + QK_K / 4),
  17: t(17, 'IQ2_XS', QK_K, 2 + QK_K / 4 + QK_K / 32),
  18: t(18, 'IQ3_XXS', QK_K, 2 + QK_K / 4 + QK_K / 8),
  19: t(19, 'IQ1_S', QK_K, 2 + QK_K / 8 + QK_K / 16),
  20: t(20, 'IQ4_NL', 32, 2 + 16), // the only IQ type with a 32-weight block
  21: t(21, 'IQ3_S', QK_K, 2 + QK_K / 4 + QK_K / 8 + QK_K / 32 + 4),
  22: t(22, 'IQ2_S', QK_K, 2 + QK_K / 4 + QK_K / 16),
  23: t(23, 'IQ4_XS', QK_K, 2 + 2 + QK_K / 2 + QK_K / 64),
  24: t(24, 'I8', 1, 1),
  25: t(25, 'I16', 1, 2),
  26: t(26, 'I32', 1, 4),
  27: t(27, 'I64', 1, 8),
  28: t(28, 'F64', 1, 8),
  29: t(29, 'IQ1_M', QK_K, QK_K / 8 + QK_K / 16 + QK_K / 32),
  30: t(30, 'BF16', 1, 2),
  // 31–33 (Q4_0_4_4 / _4_8 / _8_8) and 36–38 (IQ4_NL_*) were removed upstream.
  34: t(34, 'TQ1_0', QK_K, 2 + 4 * 13),
  35: t(35, 'TQ2_0', QK_K, 2 + 64),
  39: t(39, 'MXFP4', 32, 1 + 16), // gpt-oss
  40: t(40, 'NVFP4', 64, 4 + 32, { recent: true }),
  41: t(41, 'Q1_0', 128, 2 + 16, { recent: true }),
  42: t(42, 'Q2_0', 64, 2 + 16, { recent: true }),
};

/** One past the last id upstream knows about. Anything at or beyond is a fork. */
export const GGML_TYPE_COUNT = 43;

/**
 * Types upstream removed. They still occupy their enum slots with a block size
 * of zero, which would be a division by zero here — so they are named and
 * rejected rather than silently mis-sized. Files using them exist in the wild
 * (llama.cpp itself now refuses to load them).
 */
export const DEPRECATED_TYPES: Record<number, string> = {
  4: 'Q4_2 (removed from ggml)',
  5: 'Q4_3 (removed from ggml)',
  31: 'Q4_0_4_4 (removed — use Q4_0 with runtime repacking)',
  32: 'Q4_0_4_8 (removed — use Q4_0 with runtime repacking)',
  33: 'Q4_0_8_8 (removed — use Q4_0 with runtime repacking)',
  36: 'IQ4_NL_4_4 (removed)',
  37: 'IQ4_NL_4_8 (removed)',
  38: 'IQ4_NL_8_8 (removed)',
};

export type TypeLookup =
  | { ok: true; type: GgmlType }
  | { ok: false; reason: 'deprecated' | 'unknown'; label: string; detail: string };

export function lookupType(id: number): TypeLookup {
  const dep = DEPRECATED_TYPES[id];
  if (dep) {
    return {
      ok: false,
      reason: 'deprecated',
      label: dep.split(' ')[0],
      detail: `ggml type ${id} — ${dep}. Current llama.cpp refuses to load files using it, so its size cannot be computed.`,
    };
  }
  const type = GGML_TYPES[id];
  if (type) return { ok: true, type };
  return {
    ok: false,
    reason: 'unknown',
    label: `type ${id}`,
    detail:
      id >= GGML_TYPE_COUNT
        ? `ggml type ${id} is beyond the last type upstream llama.cpp defines (${GGML_TYPE_COUNT - 1}). This file was almost certainly produced by a fork with its own quantisation, and its block layout is unknown — guessing a size here would be worse than declining to.`
        : `ggml type ${id} is not a type this build knows about.`,
  };
}

/** Bytes on disk for a tensor of `elements` weights at type `type`. */
export function tensorBytes(type: GgmlType, elements: number): number {
  return (elements / type.blockSize) * type.typeSize;
}

/** True bits per weight, including the scales each block carries. */
export function bitsPerWeight(type: GgmlType): number {
  return (type.typeSize * 8) / type.blockSize;
}

/** Round up to the file's alignment — llama.cpp pads every tensor this way. */
export function alignUp(n: number, alignment: number): number {
  return Math.ceil(n / alignment) * alignment;
}

/**
 * `general.file_type` — the ftype the file was quantised with. It is a
 * *recipe* name, not a type, and is only worth showing as a label: two files
 * with different real contents can carry the same value (a Q4_K_M and a
 * Q4_K_L both report 15), which is exactly why this tool sizes from the tensor
 * table instead.
 */
export const FILE_TYPES: Record<number, string> = {
  0: 'F32 (all)',
  1: 'F16',
  2: 'Q4_0',
  3: 'Q4_1',
  7: 'Q8_0',
  8: 'Q5_0',
  9: 'Q5_1',
  10: 'Q2_K',
  11: 'Q3_K_S',
  12: 'Q3_K_M',
  13: 'Q3_K_L',
  14: 'Q4_K_S',
  15: 'Q4_K_M',
  16: 'Q5_K_S',
  17: 'Q5_K_M',
  18: 'Q6_K',
  19: 'IQ2_XXS',
  20: 'IQ2_XS',
  21: 'Q2_K_S',
  22: 'IQ3_XS',
  23: 'IQ3_XXS',
  24: 'IQ1_S',
  25: 'IQ4_NL',
  26: 'IQ3_S',
  27: 'IQ3_M',
  28: 'IQ2_S',
  29: 'IQ2_M',
  30: 'IQ4_XS',
  31: 'IQ1_M',
  32: 'BF16',
  36: 'TQ1_0',
  37: 'TQ2_0',
  38: 'MXFP4',
};

/** Cache types llama.cpp accepts for --cache-type-k / --cache-type-v. */
export const CACHE_TYPES: Array<{ flag: string; typeId: number; label: string }> = [
  { flag: 'f16', typeId: 1, label: 'f16 — default' },
  { flag: 'bf16', typeId: 30, label: 'bf16' },
  { flag: 'q8_0', typeId: 8, label: 'q8_0 — half the size' },
  { flag: 'q5_1', typeId: 7, label: 'q5_1' },
  { flag: 'q5_0', typeId: 6, label: 'q5_0' },
  { flag: 'q4_1', typeId: 3, label: 'q4_1' },
  { flag: 'q4_0', typeId: 2, label: 'q4_0 — quarter the size' },
  { flag: 'f32', typeId: 0, label: 'f32 — double the size' },
];

/** Bytes one cached element costs at a given cache type. */
export function cacheBytesPerElement(flag: string): number {
  const entry = CACHE_TYPES.find((c) => c.flag === flag);
  if (!entry) return 2;
  const type = GGML_TYPES[entry.typeId];
  return type.typeSize / type.blockSize;
}
